/**
 * Business time for DeliCRM (spec §9): instants are stored in UTC, business dates and
 * reporting periods follow the Europe/Kyiv calendar. Built on Intl only — no dependencies.
 */

export const BUSINESS_TIME_ZONE = 'Europe/Kyiv';

/** A Kyiv calendar date, formatted YYYY-MM-DD. */
export type BusinessDate = string;

export type PeriodKind = 'day' | 'month' | 'quarter' | 'year';

/** Half-open interval [start, end) of UTC instants. */
export interface Period {
  readonly kind: PeriodKind;
  readonly start: Date;
  readonly end: Date;
}

interface LocalDateTime {
  year: number;
  month: number; // 1–12
  day: number;
  hour: number;
  minute: number;
  second: number;
  millisecond: number;
}

const formatter = new Intl.DateTimeFormat('en-GB', {
  timeZone: BUSINESS_TIME_ZONE,
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

function toLocal(instant: Date): LocalDateTime {
  if (Number.isNaN(instant.getTime())) throw new RangeError('invalid date');
  const parts: Record<string, number> = {};
  for (const part of formatter.formatToParts(instant)) {
    if (part.type !== 'literal') parts[part.type] = Number(part.value);
  }
  return {
    year: parts.year!,
    month: parts.month!,
    day: parts.day!,
    hour: parts.hour!,
    minute: parts.minute!,
    second: parts.second!,
    millisecond: instant.getUTCMilliseconds(),
  };
}

function localAsUtcMs(local: LocalDateTime): number {
  return Date.UTC(
    local.year,
    local.month - 1,
    local.day,
    local.hour,
    local.minute,
    local.second,
    local.millisecond,
  );
}

/** Converts a Kyiv wall-clock time to the UTC instant (two passes settle DST offsets). */
function fromLocal(local: LocalDateTime): Date {
  const wall = localAsUtcMs(local);
  let guess = wall;
  for (let i = 0; i < 2; i++) {
    const offset = localAsUtcMs(toLocal(new Date(guess))) - guess;
    guess = wall - offset;
  }
  return new Date(guess);
}

const pad = (n: number, width = 2) => n.toString().padStart(width, '0');

function parseBusinessDate(date: BusinessDate): { year: number; month: number; day: number } {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) throw new RangeError(`not a business date: "${date}"`);
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const check = new Date(Date.UTC(year, month - 1, day));
  if (check.getUTCMonth() !== month - 1 || check.getUTCDate() !== day) {
    throw new RangeError(`not a calendar date: "${date}"`);
  }
  return { year, month, day };
}

const daysInMonth = (year: number, month: number) =>
  new Date(Date.UTC(year, month, 0)).getUTCDate();

/** The Kyiv calendar date of a UTC instant. */
export function businessDate(instant: Date): BusinessDate {
  const local = toLocal(instant);
  return `${pad(local.year, 4)}-${pad(local.month)}-${pad(local.day)}`;
}

/** The UTC instant of Kyiv midnight at the start of a business date. */
export function startOfBusinessDay(date: BusinessDate): Date {
  const { year, month, day } = parseBusinessDate(date);
  return fromLocal({ year, month, day, hour: 0, minute: 0, second: 0, millisecond: 0 });
}

export function addDays(date: BusinessDate, days: number): BusinessDate {
  const { year, month, day } = parseBusinessDate(date);
  const shifted = new Date(Date.UTC(year, month - 1, day + days));
  return shifted.toISOString().slice(0, 10);
}

/**
 * The same day `months` calendar months later; a day the target month doesn't have clamps to its
 * last day (31 Mar + 11 → 29 Feb in a leap year). Used for Q-points lifetimes (spec §5).
 */
export function addMonths(date: BusinessDate, months: number): BusinessDate {
  if (!Number.isInteger(months)) throw new RangeError('months must be a whole number');
  const { year, month, day } = parseBusinessDate(date);
  const index = year * 12 + (month - 1) + months;
  const y = Math.floor(index / 12);
  const m = (index % 12) + 1;
  const d = Math.min(day, daysInMonth(y, m));
  return `${pad(y, 4)}-${pad(m)}-${pad(d)}`;
}

/** Whole calendar days from `from` to `to` (negative when `to` is earlier). */
export function daysBetween(from: BusinessDate, to: BusinessDate): number {
  const a = parseBusinessDate(from);
  const b = parseBusinessDate(to);
  return Math.round(
    (Date.UTC(b.year, b.month - 1, b.day) - Date.UTC(a.year, a.month - 1, a.day)) / 86_400_000,
  );
}

function periodStartMonth(kind: Exclude<PeriodKind, 'day'>, month: number): number {
  if (kind === 'year') return 1;
  if (kind === 'quarter') return month - ((month - 1) % 3);
  return month;
}

const monthsIn: Record<Exclude<PeriodKind, 'day'>, number> = { month: 1, quarter: 3, year: 12 };

function shiftMonths(year: number, month: number, delta: number) {
  const index = year * 12 + (month - 1) + delta;
  return { year: Math.floor(index / 12), month: (index % 12) + 1 };
}

/** The Kyiv day / month / quarter / year containing an instant, as a UTC interval. */
export function periodOf(kind: PeriodKind, instant: Date): Period {
  const local = toLocal(instant);
  if (kind === 'day') {
    const day = businessDate(instant);
    return { kind, start: startOfBusinessDay(day), end: startOfBusinessDay(addDays(day, 1)) };
  }
  const startMonth = periodStartMonth(kind, local.month);
  const midnight = { day: 1, hour: 0, minute: 0, second: 0, millisecond: 0 };
  const next = shiftMonths(local.year, startMonth, monthsIn[kind]);
  return {
    kind,
    start: fromLocal({ year: local.year, month: startMonth, ...midnight }),
    end: fromLocal({ ...next, ...midnight }),
  };
}

/** Current period so far: [start of the period containing `now`, `now`). */
export function periodToDate(kind: PeriodKind, now: Date): Period {
  return { kind, start: periodOf(kind, now).start, end: now };
}

/**
 * The comparable span of the previous period (M2M, Q2Q, Y2Y): from the start of the previous
 * period to the same offset into it as `now` is into the current one. The day of month is
 * clamped when the previous month is shorter (31 March → 28/29 February).
 */
export function samePointInPreviousPeriod(kind: PeriodKind, now: Date): Period {
  if (kind === 'day') {
    const today = businessDate(now);
    const local = toLocal(now);
    const yesterday = parseBusinessDate(addDays(today, -1));
    return {
      kind,
      start: startOfBusinessDay(addDays(today, -1)),
      end: fromLocal({ ...local, ...yesterday }),
    };
  }
  const local = toLocal(now);
  const shifted = shiftMonths(local.year, local.month, -monthsIn[kind]);
  const day = Math.min(local.day, daysInMonth(shifted.year, shifted.month));
  const end = fromLocal({ ...local, ...shifted, day });
  return { kind, start: periodOf(kind, end).start, end };
}
